import { z } from "zod";
import { customerItineraryValidationRunSchema } from "./itinerary-validation-run.js";
// The itinerary edit contract of Milestone D (CHT-742; docs/architecture/milestone-d-contracts.md sections 3.2 to 3.6 and 8): the
// customer routes MD-I1 to MD-I6 and the pieces the staff routes share (`staff-itinerary.ts`).
//
// Same rules as the other customer contracts: strict objects (a key that is not listed fails the parse, in a request and in a
// response), ISO 8601 text for instants, codes repeated from the domain. A time is a WALL time: `{ date, time, timeZone }`. No shape
// here takes a UTC instant, a device zone, a provenance, a staff note, a staff id, a provider, a place id or a confirmation from a
// client, and no customer response carries one.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
export const itineraryUuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const localDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);
const localTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/u);
/** `YYYY-MM-DDTHH:MM`, the form a wall time is shown and stored in. */
const localDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u);
/** The shape of an IANA zone name; the server asks the runtime whether the zone exists. The text is stored as given. */
const timeZone = z.string().regex(/^(UTC|[A-Za-z_]+(\/[A-Za-z0-9_+-]+){1,2})$/u);
const version = z.number().int().min(1).max(2_147_483_647);
const text = (max) => z.string().max(max);
// --- codes (repeated from the domain, parity-tested) ----------------------------------------------------------------------
export const itineraryStatusValues = ["DRAFT", "ACTIVE", "SUPERSEDED", "ARCHIVED"];
/** How an item reacts to weather (CHT-745). Customer and staff set it; the agent only inside the closed list the application checks. */
export const weatherSensitivityValues = ["NONE", "OUTDOOR", "WATER", "HEIGHT"];
export const itineraryItemKindValues = [
    "CATALOG_ITEM",
    "EXTERNAL_PLACE",
    "TRANSIT",
    "BUFFER",
    "CUSTOM_ACTIVITY",
    "CONCIERGE_REQUEST",
];
export const itineraryItemStatusValues = ["PLANNED", "TENTATIVE", "CANCELLED"];
export const itineraryTimingKindValues = ["SCHEDULED", "ALL_DAY", "UNTIMED"];
export const itinerarySectionKindValues = ["DAY", "FLEXIBLE"];
export const itineraryFeasibilityValues = ["UNKNOWN", "FEASIBLE", "INFEASIBLE"];
export const itineraryEvidenceStateValues = ["VERIFIED", "UNVERIFIED", "STALE"];
export const itineraryEditModeValues = ["PROPOSE", "APPLY"];
export const itineraryEditOperationValues = [
    "ADD_SECTION",
    "REMOVE_SECTION",
    "ADD_ITEM",
    "EDIT_ITEM",
    "MOVE_ITEM",
    "REMOVE_ITEM",
];
export const itineraryWallTimeDisambiguationValues = ["reject", "earlier", "later"];
export const itineraryIssueSeverityValues = ["BLOCKING", "WARNING"];
export const itineraryTimeZoneSourceValues = ["CATALOG", "STAFF", "CUSTOMER"];
export const MAX_ITINERARY_OPERATIONS = 100;
export const MAX_ITINERARY_SECTIONS = 100;
export const MAX_ITINERARY_ITEMS_PER_SECTION = 200;
// --- inputs ---------------------------------------------------------------------------------------------------------------
/** A wall time in the zone it is read in. An overlap needs `disambiguation` (`earlier` or `later`); a gap time is refused. */
export const itineraryWallTimeSchema = z.strictObject({
    date: localDate,
    time: localTime,
    timeZone,
    disambiguation: z.enum(itineraryWallTimeDisambiguationValues).optional(),
});
/** An end is a wall time (a flight lands in the arrival zone) or an elapsed duration, never both. */
export const itineraryTimingSchema = z.discriminatedUnion("timingKind", [
    z.strictObject({ timingKind: z.literal("UNTIMED") }),
    z.strictObject({ timingKind: z.literal("ALL_DAY") }),
    z.strictObject({
        timingKind: z.literal("SCHEDULED"),
        start: itineraryWallTimeSchema,
        end: itineraryWallTimeSchema.nullish(),
        durationMinutes: z.number().int().min(1).max(10_080).nullish(),
    }),
]);
const externalPlaceEntry = z.strictObject({
    provider: z
        .string()
        .regex(/^[a-z][a-z0-9_]*$/u)
        .max(64),
    id: text(255),
    name: text(200),
});
/** What every actor may send for an item. No provenance, no row id, no instant, no observation time, no creator. */
export const itineraryItemEntryShape = {
    kind: z.enum(itineraryItemKindValues),
    itemStatus: z.enum(itineraryItemStatusValues).optional(),
    /** Required unless the item is a catalog item (the server fills the catalog name). */
    title: text(200).nullish(),
    timing: itineraryTimingSchema,
    locationName: text(200).nullish(),
    locality: text(100).nullish(),
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/u)
        .nullish(),
    catalogItemId: itineraryUuid.nullish(),
    externalPlace: externalPlaceEntry.nullish(),
    conciergeRequestId: itineraryUuid.nullish(),
    customerNote: text(1000).nullish(),
    /** A fixed commitment the customer holds: touching it asks for confirmation. Never set by the agent. */
    isFixed: z.boolean().optional(),
    /** How the item reacts to weather. The agent may name only `NONE`, or `OUTDOOR` for a new item of a closed list of kinds. */
    weatherSensitivity: z.enum(weatherSensitivityValues).optional(),
};
/** A customer or the agent: no staff note, no coordinates (Lafayette-owned geography is entered by staff). */
export const customerItineraryItemEntrySchema = z.strictObject(itineraryItemEntryShape);
/** An EDIT_ITEM changes only the fields it names. `kind`, the catalog link and the request link never change. */
export const itineraryItemChangesShape = {
    title: text(200).optional(),
    itemStatus: z.enum(itineraryItemStatusValues).optional(),
    customerNote: text(1000).nullable().optional(),
    locationName: text(200).nullable().optional(),
    locality: text(100).nullable().optional(),
    countryCode: z
        .string()
        .regex(/^[A-Z]{2}$/u)
        .nullable()
        .optional(),
    isFixed: z.boolean().optional(),
    weatherSensitivity: z.enum(weatherSensitivityValues).optional(),
    timing: itineraryTimingSchema.optional(),
};
export const customerItineraryItemChangesSchema = z.strictObject(itineraryItemChangesShape);
/** A section of the base plan: by its id, by its date, or the undated group. */
export const itinerarySectionRefSchema = z.union([
    z.strictObject({ sectionId: itineraryUuid }),
    z.strictObject({ localDate }),
    z.strictObject({ flexible: z.literal(true) }),
]);
export const itinerarySectionInputSchema = z.discriminatedUnion("kind", [
    z.strictObject({
        kind: z.literal("DAY"),
        localDate,
        timeZone,
        title: text(140).nullish(),
    }),
    z.strictObject({ kind: z.literal("FLEXIBLE"), title: text(140).nullish() }),
]);
/** The operations of an edit (builds both the customer and the staff list; `entry` and `changes` differ by who may write what). */
export function itineraryEditOperationSchema(entry, changes) {
    return z.discriminatedUnion("op", [
        z.strictObject({ op: z.literal("ADD_SECTION"), section: itinerarySectionInputSchema }),
        z.strictObject({ op: z.literal("REMOVE_SECTION"), section: itinerarySectionRefSchema }),
        z.strictObject({
            op: z.literal("ADD_ITEM"),
            section: itinerarySectionRefSchema,
            position: z.number().int().min(0).max(999).optional(),
            item: entry,
        }),
        z.strictObject({ op: z.literal("EDIT_ITEM"), stableId: itineraryUuid, changes }),
        z.strictObject({
            op: z.literal("MOVE_ITEM"),
            stableId: itineraryUuid,
            toSection: itinerarySectionRefSchema.optional(),
            toPosition: z.number().int().min(0).max(999).optional(),
            start: itineraryWallTimeSchema.optional(),
        }),
        z.strictObject({ op: z.literal("REMOVE_ITEM"), stableId: itineraryUuid }),
    ]);
}
export const customerItineraryEditOperationSchema = itineraryEditOperationSchema(customerItineraryItemEntrySchema, customerItineraryItemChangesSchema);
/** The plan to build or to replace with: sections with their items. An item named by `stableId` keeps its lineage. */
export function itineraryPlanSectionSchema(entry) {
    return z.strictObject({
        section: itinerarySectionInputSchema,
        items: z
            .array(z.strictObject({ stableId: itineraryUuid.optional(), item: entry }))
            .max(MAX_ITINERARY_ITEMS_PER_SECTION),
    });
}
export const itineraryReplaceTargetSchema = z.strictObject({
    baseItineraryId: itineraryUuid,
    expectedRevision: version,
});
/** `POST /customer/trips/:tripId/itinerary/plan` (MD-I1): a new customer DRAFT, or with `replace` a replace-plan. */
export const customerItineraryPlanBodySchema = z.strictObject({
    title: text(140).min(1),
    sections: z
        .array(itineraryPlanSectionSchema(customerItineraryItemEntrySchema))
        .max(MAX_ITINERARY_SECTIONS),
    replace: itineraryReplaceTargetSchema.nullish(),
});
/**
 * `POST /customer/trips/:tripId/itinerary/edits` (MD-I2). `APPLY` revises and activates in one atomic step and is refused with
 * `409` when the change is consequential (propose it instead); `PROPOSE` stores a DRAFT the customer then activates.
 */
export const customerItineraryEditBodySchema = z.strictObject({
    mode: z.enum(itineraryEditModeValues),
    baseItineraryId: itineraryUuid,
    expectedRevision: version,
    operations: z.array(customerItineraryEditOperationSchema).min(1).max(MAX_ITINERARY_OPERATIONS),
});
/** `POST /customer/itineraries/:itineraryId/activate` (MD-I3): the version the customer read. */
export const customerItineraryActivateBodySchema = z.strictObject({ expectedVersion: version });
/** `POST /customer/itineraries/:itineraryId/discard` (MD-I4). A version, when given, must be current. */
export const customerItineraryDiscardBodySchema = z.strictObject({
    expectedVersion: version.optional(),
});
/** `POST /customer/itineraries/:itineraryId/validate` (MD-I5). No input. */
export const customerItineraryValidateBodySchema = z.strictObject({});
// --- outputs --------------------------------------------------------------------------------------------------------------
/**
 * The outcome of a command, in ids, versions and codes (never a title or a note). `addedStableIds` are the lineage ids that new
 * items got (at most 20 are listed; `addedCount` is the whole count). `requiresCustomerConfirmation` is true for a customer or
 * agent proposal of a consequential change: the customer confirms it with `activate`.
 */
export const customerItineraryOutcomeSchema = z.strictObject({
    itineraryId: itineraryUuid,
    baseItineraryId: itineraryUuid.nullable(),
    supersededItineraryId: itineraryUuid.nullable(),
    revision: version.nullable(),
    version,
    status: z.enum(itineraryStatusValues),
    itemCount: z.number().int().min(0).nullable(),
    addedStableIds: z.array(itineraryUuid).max(20),
    addedCount: z.number().int().min(0),
    consequential: z.boolean().nullable(),
    requiresCustomerConfirmation: z.boolean().nullable(),
    blockingCount: z.number().int().min(0).nullable(),
    warningCount: z.number().int().min(0).nullable(),
    issueCodes: z.array(z.string().min(1).max(64)).max(64),
});
const evidenceSchema = z.strictObject({
    state: z.enum(itineraryEvidenceStateValues),
    observedAt: instant.nullable(),
});
/**
 * One item as a customer reads it. The key order is the order of `CUSTOMER_ITINERARY_ITEM_VIEW_KEYS` of the application package
 * (a parity test pins it). `stableId` is what the customer and the agent address the item by; `id` is the row of this revision.
 * `localStart` and `localEnd` are computed on the server from the stored instant, `localStart` in the item's own zone and `localEnd`
 * in `endTimeZone` (the zone the end is read in: the start zone unless the item ends in another one, like a flight); the entered wall times
 * appear only when the runtime's zone data now reads them differently. `evidence` is `UNVERIFIED` until a route or weather check ran.
 */
export const customerItineraryItemViewSchema = z.strictObject({
    id: itineraryUuid,
    position: z.number().int().min(0),
    kind: z.enum(itineraryItemKindValues),
    status: z.enum(itineraryItemStatusValues),
    title: z.string().min(1).max(200),
    timing: z.strictObject({
        kind: z.enum(itineraryTimingKindValues),
        startsAt: instant.nullable(),
        endsAt: instant.nullable(),
        timeZone: timeZone.nullable(),
        durationMinutes: z.number().int().min(1).max(10_080).nullable(),
    }),
    location: z.strictObject({
        name: z.string().nullable(),
        locality: z.string().nullable(),
        countryCode: z.string().nullable(),
        /** Present only for Lafayette-owned geography; `null` for a place chosen through a provider. */
        latitude: z.number().nullable(),
        longitude: z.number().nullable(),
    }),
    catalogItem: z
        .strictObject({
        id: itineraryUuid,
        name: z.string(),
        itemType: z.string().min(1).max(64),
        available: z.boolean(),
    })
        .nullable(),
    externalPlace: z
        .strictObject({ provider: z.string().min(1).max(64), name: z.string() })
        .nullable(),
    conciergeRequestId: itineraryUuid.nullable(),
    customerNote: z.string().nullable(),
    stableId: itineraryUuid,
    isFixed: z.boolean(),
    localStart: localDateTime.nullable(),
    localEnd: localDateTime.nullable(),
    endTimeZone: timeZone.nullable(),
    enteredLocalStart: localDateTime.nullable(),
    enteredLocalEnd: localDateTime.nullable(),
    evidence: z.strictObject({ route: evidenceSchema, weather: evidenceSchema }),
});
/** The key order of the customer item view (parity-tested against `CUSTOMER_ITINERARY_ITEM_VIEW_KEYS`). */
export const CUSTOMER_ITINERARY_ITEM_SCHEMA_KEYS = Object.keys(customerItineraryItemViewSchema.shape);
export const customerItinerarySectionViewSchema = z.strictObject({
    id: itineraryUuid,
    position: z.number().int().min(0),
    kind: z.enum(itinerarySectionKindValues),
    localDate: localDate.nullable(),
    timeZone: timeZone.nullable(),
    title: z.string().nullable(),
    items: z.array(customerItineraryItemViewSchema).max(1_000),
});
export const customerItineraryViewSchema = z.strictObject({
    id: itineraryUuid,
    tripId: itineraryUuid,
    status: z.enum(itineraryStatusValues),
    revision: version,
    title: z.string().min(1).max(200),
    sections: z.array(customerItinerarySectionViewSchema).max(366),
    activatedAt: instant.nullable(),
});
export const CUSTOMER_ITINERARY_SCHEMA_KEYS = Object.keys(customerItineraryViewSchema.shape);
/**
 * `GET /customer/trips/:tripId/itinerary` (MD-I6): the plan of record or the customer's newest draft, or `null`. The plan of record
 * is edited against its `revision`; a draft is activated against its `version`.
 */
export const customerTripItinerarySchema = z.strictObject({
    itinerary: customerItineraryViewSchema.nullable(),
    version: version.nullable(),
});
/**
 * `GET /customer/itineraries/:itineraryId` (MD-I13): one itinerary of the signed-in customer by id, in the same customer view as the
 * trip read: a customer-visible DRAFT (a proposal) or an ACTIVE or SUPERSEDED revision. There is always a plan (a missing,
 * foreign, staff-only or archived id is a `404`). The `version` is the token a draft is activated or discarded with.
 */
export const customerItineraryByIdSchema = z.strictObject({
    itinerary: customerItineraryViewSchema,
    version,
});
export const itineraryIssueSchema = z.strictObject({
    code: z.string().min(1).max(64),
    severity: z.enum(itineraryIssueSeverityValues),
    sectionId: itineraryUuid.nullable(),
    itemId: itineraryUuid.nullable(),
});
/** `POST /customer/itineraries/:itineraryId/validate` (MD-I5): the structural and temporal issues (route and weather checks come later). */
export const customerItineraryValidationSchema = z.strictObject({
    itineraryId: itineraryUuid,
    itineraryVersion: version,
    feasibility: z.enum(itineraryFeasibilityValues),
    issues: z.array(itineraryIssueSchema).max(1_000),
    /** CHT-746: the route and weather evidence of the run. Absent when the server runs no evidence checks (providers not composed). */
    run: customerItineraryValidationRunSchema.optional(),
});
// --- refusals -------------------------------------------------------------------------------------------------------------
/** The rules an itinerary command can refuse, with the typed body `itineraryRefusal` beside the error envelope (409 or 422). */
export const itineraryRefusalCodeValues = [
    "itinerary.version_conflict",
    "itinerary.requires_confirmation",
    "itinerary.item_linked",
    "itinerary.not_mutable",
    "itinerary.base_not_active",
    "itinerary.trip_closed",
    "itinerary.active_exists",
    "itinerary.invalid_structure",
    "itinerary.item_not_found",
    "itinerary.section_not_found",
    "itinerary.time_not_existent",
    "itinerary.time_ambiguous",
    "itinerary.invalid_input",
];
/**
 * Why a command was refused, in a code the app and the agent can act on. `field` names the input that failed (never its value).
 * `suggestion` is, for `itinerary.time_not_existent`, the first valid local time after the clock gap (`YYYY-MM-DDTHH:MM`): a
 * suggestion to show the customer, never a silent fix. For `itinerary.time_ambiguous` the app must ask the customer which of the
 * two times they mean (`earlier` or `later`).
 */
export const itineraryRefusalSchema = z.strictObject({
    code: z.enum(itineraryRefusalCodeValues),
    field: z.string().min(1).max(64).nullable(),
    suggestion: localDateTime.nullable(),
});
